import React, { useEffect, useState } from "react";
import { Page, Layout, Card, TextField, TextContainer, BlockStack, Checkbox } from "@shopify/polaris";
import { NewShopForm } from "./support/NewShopForm";
import { CurrencyChange } from "./support/CurrencyChange";
import { InventorySyncOptions } from "./support/InventorySyncOptions";
import { SyncbackOptions } from "./support/SyncbackOptions";
import { CostSyncOptions } from "./support/CostSyncOptions";
import { updateUser, uploadLogo, useSettings } from "../../api_utils/requests";
import ImportProgressBanner from "../home/support/ImportProgressBanner";
import { ErrorBanner } from "../common/ErrorBanner";
import { Footer } from "../common/Footer";
import { SettingsPageSkeleton } from "../common/skeletons";
import { SETTINGS_HELP_PAGE_URL } from "../../constants";
import { extractMessageFromError } from "../../helper_functions/utils";
import type { User } from "../../api_utils/types";
import UploadBrandLogo from "./UploadBrandLogo";
import SaveBar from "../common/SaveBar";

function Settings(): React.ReactElement {
  const [isDirty, setIsDirty] = useState(false);
  const [brandFile, setBrandFile] = useState<File>(null);
  const [user, setUser] = useState<User>(null);
  const [originalUser, setOriginalUser] = useState<User>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const {
    data: { currentUser, currencies, shopifyShops },
    isLoading: pageLoading,
  } = useSettings();

  useEffect(() => {
    setOriginalUser({ ...currentUser });
    setUser({ ...currentUser });
  }, [currentUser]);

  const handleSave = (): Promise<void> => handleUpload();

  const handleUpload = (): Promise<void> => {
    return Promise.all([updateUserData(), uploadBrandLogo()]).then(() => {
      setErrorMessage("");
      setIsDirty(false);
    }).catch((err: unknown) => {
      Rollbar.error(err, { userId: currentUser.id });
      const newErrorMessage = extractMessageFromError(err);
      setErrorMessage(newErrorMessage);
    });
  };

  const uploadBrandLogo = (): Promise<void> => {
    if (brandFile) {
      const formData = new FormData();
      formData.append("brandLogo", brandFile);
      return uploadLogo(currentUser.id, formData)
        .then((response) => {
          setUser((prevUser) => ({ ...prevUser, brandLogo: response.brandLogo }));
        })
    } else {
      return Promise.resolve();
    }
  };

  const updateUserData = (aws_image_url?: string): Promise<void> => {
    return updateUser(currentUser.id, {
      currency: user?.currency,
      company_name: user?.companyName,
      company_address: user?.companyAddress,
      sync_level: user?.syncLevel,
      syncback_setting: user?.syncbackSetting,
      costsync_setting: user?.costsyncSetting,
      brand_logo: aws_image_url || user?.brandLogo,
      email: user?.email,
      include_cost_adjustment: user?.includeCostAdjustment,
      recommendation_subscription: user?.recommendation_subscription,
    }).then((response) => {
      shopify.toast.show("Updated");
      setUser({ ...response.user });
      setOriginalUser({ ...response.user });
    });
  };

  const handleDiscard = (): void => {
    setUser(originalUser);
    setBrandFile(null);
    setIsDirty(false);
  };

  const handleSyncOptionChange = (_checked: boolean, newValue: string): void => {
    setUser({ ...user, syncLevel: newValue });
    setIsDirty(true);
  };

  const handleSyncbackOptionChange = (_checked: boolean, newValue: string): void => {
    setUser({ ...user, syncbackSetting: newValue });
    setIsDirty(true);
  };

  const handleCostsyncOptionChange = (_checked: boolean, newValue: string): void => {
    setUser({ ...user, costsyncSetting: newValue });
    setIsDirty(true);
  };

  const handleCurrencyChange = (value: string): void => {
    setUser({ ...user, currency: value });
    setIsDirty(true);
  };

  const handleCompanyDetails = (value: string, id: string): void => {
    if (id === "company_name") {
      setUser({ ...user, companyName: value });
    } else {
      setUser({ ...user, companyAddress: value });
    }
    setIsDirty(true);
  };

  const handleEmailChange = (value: string): void => {
    setUser({ ...user, email: value });
    setIsDirty(true);
  };

  const handleCostAdjustment = (_checked: boolean, newValue: string): void => {
    const value = newValue === "yes" ? true : false;
    setUser({ ...user, includeCostAdjustment: value });
    setIsDirty(true);
  };

  const handleCheckboxChange = (checked: boolean): void => {
    setUser({ ...user, recommendation_subscription: checked });
    setIsDirty(true);
  };

  const handleRemoveImage = (): void => {
    setBrandFile(null);
    setUser((prevUser) => ({ ...prevUser, brandLogo: null }));
    setIsDirty(true);
  };

  const pageMarkup = currentUser && (
    <Page title="Settings">
      <ImportProgressBanner />
      <SaveBar handleDiscard={handleDiscard} handleSave={handleSave} isDirty={isDirty} />

      <Layout>
        {errorMessage ? (
          <Layout.Section variant="fullWidth">
            <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
          </Layout.Section>
        ) : null}
        <Layout.AnnotatedSection
          description="This is the currency that all values will appear as."
          title="Currency option"
        >
          <CurrencyChange
            currencies={currencies}
            currency={user?.currency}
            handleCurrencyChange={handleCurrencyChange}
          />
        </Layout.AnnotatedSection>
        <br />
        <Layout.AnnotatedSection description="Choose how Horse should sync to Shopify" title="Sync options">
          <InventorySyncOptions
            handleSyncOptionChange={handleSyncOptionChange}
            shopifyShops={shopifyShops}
            userSyncOption={user?.syncLevel}
          />
          <SyncbackOptions
            handleSyncOptionChange={handleSyncbackOptionChange}
            syncbackSetting={user?.syncbackSetting}
          />
          <CostSyncOptions
            costsyncSetting={user?.costsyncSetting}
            handleCostAdjustment={handleCostAdjustment}
            handleSyncOptionChange={handleCostsyncOptionChange}
            user={user}
          />
        </Layout.AnnotatedSection>
        <br />
        <Layout.AnnotatedSection
          description="Connect multiple stores for them to share inventory."
          title="Connected stores"
        >
          <NewShopForm currentUser={currentUser} shopifyShops={shopifyShops} />
        </Layout.AnnotatedSection>
        <br />
        <Layout.AnnotatedSection
          description="These details will take precedence of your store details on purchase order PDFs"
          title="Company details"
        >
          <Card>
            <TextField
              autoComplete="off"
              id="company_name"
              label="Company name"
              onChange={handleCompanyDetails}
              value={user?.companyName}
            />
            <br />
            <TextField
              autoComplete="off"
              id="company_address"
              label="Company Address"
              onChange={handleCompanyDetails}
              value={user?.companyAddress}
            />
            <br />
            <UploadBrandLogo
              brandFile={brandFile}
              brandLogo={user?.brandLogo}
              handleRemoveImage={handleRemoveImage}
              setBrandFile={setBrandFile}
              setIsDirty={setIsDirty}
            />
          </Card>
        </Layout.AnnotatedSection>
        <br />
        <Layout.AnnotatedSection description="Your (default) email address when emailing PDFs" title="User details">
          <Card>
            <TextField
              autoComplete="off"
              label="Email Address"
              onChange={handleEmailChange}
              type="email"
              value={user?.email}
            />
            <TextContainer>
              <BlockStack>
                <Checkbox
                  checked={user?.recommendation_subscription}
                  label="Enable recommendation email"
                  onChange={handleCheckboxChange}
                />
              </BlockStack>
            </TextContainer>
          </Card>
        </Layout.AnnotatedSection>
      </Layout>
      <Footer pageTitle="settings" url={SETTINGS_HELP_PAGE_URL} />
    </Page>
  );

  return pageLoading ? <SettingsPageSkeleton /> : pageMarkup;
}

export default Settings;
